import { useCallback, useEffect, useRef, useState } from 'react';
import type { AgentDraftPayload } from '@reflex/shared';
import { isDraftContentEmpty } from '@/components/agent/launch-agent-dialog/draft-payload';
import { useUpsertAgentDraft, useDeleteAgentDraft } from './useAgentDrafts';

/** Backend autosave debounce (Notion / Google Docs style). */
const DEBOUNCE_MS = 1500;

export type DraftAutosaveStatus = 'saved' | 'unsaved' | 'saving' | 'error';

export interface UseAgentDraftAutosaveOptions {
  enabled: boolean;
  /** When false, skip autosave until the form has a baseline (loaded draft or defaults). */
  ready: boolean;
  /** True when the form differs from its open baseline. */
  isDirty: boolean;
  /** Changes when form content changes so debounce can reschedule. */
  revision: unknown;
  getPayload: () => AgentDraftPayload;
  draftId?: string | null;
  onDraftId?: (id: string | null) => void;
}

/**
 * Form state captured synchronously by the caller at `flush` time. Dismissing
 * the launch dialog tears down its form state (`ready`, `isDirty`, `draftId`)
 * in the same event that asks for the flush, so a save that re-reads those
 * values when it finally runs would persist a stale or orphaned draft.
 */
export interface DraftFlushInput {
  payload: AgentDraftPayload;
  isDirty: boolean;
}

export interface UseAgentDraftAutosaveReturn {
  draftId: string | null;
  flush: (input: DraftFlushInput) => Promise<void>;
  discard: () => Promise<void>;
  saveStatus: DraftAutosaveStatus;
}

/** A save's inputs, frozen at enqueue time so the chain can't read them late. */
interface SaveSnapshot extends DraftFlushInput {
  draftId: string | null;
}

export function useAgentDraftAutosave({
  enabled,
  ready,
  isDirty,
  revision,
  getPayload,
  draftId,
  onDraftId,
}: UseAgentDraftAutosaveOptions): UseAgentDraftAutosaveReturn {
  const upsert = useUpsertAgentDraft();
  const deleteDraft = useDeleteAgentDraft();
  const [saveStatus, setSaveStatus] = useState<DraftAutosaveStatus>(() =>
    draftId ? 'saved' : 'saved',
  );
  const draftIdRef = useRef<string | null>(draftId ?? null);
  const clientSeqRef = useRef(0);
  const lastAppliedSeqRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  const upsertRef = useRef(upsert.mutateAsync);
  const deleteDraftRef = useRef(deleteDraft.mutateAsync);
  const getPayloadRef = useRef(getPayload);
  const onDraftIdRef = useRef(onDraftId);
  const enabledRef = useRef(enabled);
  const readyRef = useRef(ready);
  const isDirtyRef = useRef(isDirty);

  useEffect(() => {
    upsertRef.current = upsert.mutateAsync;
  }, [upsert.mutateAsync]);

  useEffect(() => {
    deleteDraftRef.current = deleteDraft.mutateAsync;
  }, [deleteDraft.mutateAsync]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    readyRef.current = ready;
  }, [ready]);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    draftIdRef.current = draftId ?? null;
  }, [draftId]);

  // Sync saveStatus with draft/dirty transitions during render so callers see
  // the updated status immediately (avoids setState-in-effect lint + microtask lag).
  const [prevDraftId, setPrevDraftId] = useState(draftId);
  if (draftId !== prevDraftId) {
    setPrevDraftId(draftId);
    if (draftId) {
      setSaveStatus('saved');
    }
  }

  const isCleanSaved = enabled && ready && !isDirty && Boolean(draftId);
  const [prevIsCleanSaved, setPrevIsCleanSaved] = useState(isCleanSaved);
  if (isCleanSaved && !prevIsCleanSaved) {
    setSaveStatus('saved');
  }
  if (isCleanSaved !== prevIsCleanSaved) {
    setPrevIsCleanSaved(isCleanSaved);
  }

  const [prevIsDirty, setPrevIsDirty] = useState(isDirty);
  if (isDirty && !prevIsDirty && enabled && ready) {
    setSaveStatus((prev) => (prev === 'saving' ? 'saving' : 'unsaved'));
  }
  if (isDirty !== prevIsDirty) {
    setPrevIsDirty(isDirty);
  }

  useEffect(() => {
    getPayloadRef.current = getPayload;
  }, [getPayload]);

  useEffect(() => {
    onDraftIdRef.current = onDraftId;
  }, [onDraftId]);

  const enqueueSave = useCallback((snapshot?: SaveSnapshot) => {
    saveChainRef.current = saveChainRef.current
      .then(async () => {
        // A snapshot is a forced flush: the caller already committed to saving,
        // so honor it even though the form it came from has since been reset.
        if (!snapshot && (!enabledRef.current || !readyRef.current)) return;

        const payload = snapshot?.payload ?? getPayloadRef.current();
        // Prefer the live id: a save earlier in the chain may have just created
        // the draft. Fall back to the snapshot for when teardown cleared it.
        const id = draftIdRef.current ?? snapshot?.draftId ?? null;

        if (isDraftContentEmpty(payload)) {
          if (!id) return;
          setSaveStatus('saving');
          try {
            await deleteDraftRef.current(id);
            draftIdRef.current = null;
            onDraftIdRef.current?.(null);
            if (enabledRef.current) {
              setSaveStatus('saved');
            }
          } catch (error) {
            console.warn('Failed to delete empty agent draft:', error);
            if (enabledRef.current) {
              setSaveStatus('error');
            }
          }
          return;
        }

        if (!(snapshot?.isDirty ?? isDirtyRef.current)) return;

        const seq = ++clientSeqRef.current;
        setSaveStatus('saving');
        try {
          const res = await upsertRef.current({
            id: id ?? undefined,
            payload,
            clientSeq: seq,
          });
          if (res.clientSeq < lastAppliedSeqRef.current) {
            return;
          }
          lastAppliedSeqRef.current = res.clientSeq;
          draftIdRef.current = res.draft.id;
          onDraftIdRef.current?.(res.draft.id);
          if (enabledRef.current) {
            setSaveStatus('saved');
          }
        } catch (error) {
          console.warn('Failed to save agent draft:', error);
          if (enabledRef.current) {
            setSaveStatus('error');
          }
        }
      })
      .catch(() => {
        // Keep the save chain alive after a rejected save.
      });
    return saveChainRef.current;
  }, []);

  const enqueueSaveRef = useRef(enqueueSave);
  useEffect(() => {
    enqueueSaveRef.current = enqueueSave;
  }, [enqueueSave]);

  const scheduleSave = useCallback(() => {
    if (!enabledRef.current || !readyRef.current || !isDirtyRef.current) return;

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      void enqueueSaveRef.current();
    }, DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    if (!enabled || !ready) return;

    if (isDirty) {
      scheduleSave();
      return () => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      };
    }

    // Form matches baseline but a draft row still exists with no message content.
    if (draftIdRef.current && isDraftContentEmpty(getPayloadRef.current())) {
      void enqueueSaveRef.current();
    }
  }, [enabled, ready, isDirty, revision, scheduleSave]);

  const flush = useCallback(async (input: DraftFlushInput): Promise<void> => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (!enabledRef.current || !readyRef.current) return;
    await enqueueSaveRef.current({
      payload: input.payload,
      isDirty: input.isDirty,
      draftId: draftIdRef.current,
    });
  }, []);

  const discard = useCallback(async (): Promise<void> => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    await saveChainRef.current;
    const id = draftIdRef.current;
    draftIdRef.current = null;
    setSaveStatus('saved');
    if (!id) return;
    try {
      await deleteDraft.mutateAsync(id);
    } catch (error) {
      console.warn('Failed to delete agent draft:', error);
    }
  }, [deleteDraft]);

  useEffect(() => {
    if (!enabled) return;
    const onBeforeUnload = () => {
      if (!readyRef.current || !isDirtyRef.current) return;
      void enqueueSaveRef.current();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (enabledRef.current && readyRef.current && isDirtyRef.current) {
        void enqueueSaveRef.current();
      }
    };
  }, [enabled]);

  return {
    draftId: draftId ?? null,
    flush,
    discard,
    saveStatus,
  };
}
